const finiteNumber = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const GEO_FENCE_LOCATION_FRESHNESS_MS = 5 * 60 * 1000;
export const GEO_FENCE_FUTURE_CLOCK_SKEW_MS = 30 * 1000;

export const geoFenceLocationTimestampMs = (location) => {
  const value = location?.timestamp
    ?? location?.updated_at
    ?? location?.last_updated
    ?? location?.recorded_at;
  if (value == null || value === '') return null;
  const timestamp = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const geoFenceLocationState = (
  location,
  nowMs = Date.now(),
  maximumAgeMs = GEO_FENCE_LOCATION_FRESHNESS_MS,
) => {
  if (!location) return { live: false, reason: 'missing', observedAt: null, ageMs: null };
  if (location?.is_active === false) return { live: false, reason: 'inactive', observedAt: null, ageMs: null };
  if (String(location?.status || '').trim().toLowerCase() === 'offline') {
    return {
      live: false,
      reason: 'offline',
      observedAt: geoFenceLocationTimestampMs(location),
      ageMs: null,
    };
  }
  const observedAt = geoFenceLocationTimestampMs(location);
  if (observedAt == null) return { live: false, reason: 'missing_timestamp', observedAt: null, ageMs: null };
  const ageMs = Number(nowMs) - observedAt;
  if (ageMs < -GEO_FENCE_FUTURE_CLOCK_SKEW_MS) {
    return { live: false, reason: 'future_timestamp', observedAt, ageMs };
  }
  if (ageMs > Number(maximumAgeMs)) {
    return { live: false, reason: 'stale', observedAt, ageMs };
  }
  const lat = finiteNumber(location?.lat);
  const lng = finiteNumber(location?.lng);
  if (lat == null || lng == null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return { live: false, reason: 'invalid_coordinates', observedAt, ageMs };
  }
  return { live: true, reason: 'live', observedAt, ageMs, lat, lng };
};

export const isFreshGeoFenceLocation = (
  location,
  nowMs = Date.now(),
  maximumAgeMs = GEO_FENCE_LOCATION_FRESHNESS_MS,
) => geoFenceLocationState(location, nowMs, maximumAgeMs).live;

// Freshness controls marker presentation, not whether a real last-known
// coordinate can be used by the geo-fence.
export const isMappableGeoFenceLocation = (location) => {
  if (!location || location?.is_admin_team_marker) return false;
  const lat = finiteNumber(location?.lat);
  const lng = finiteNumber(location?.lng);
  return lat != null
    && lng != null
    && lat >= -90
    && lat <= 90
    && lng >= -180
    && lng <= 180;
};

export const mergeGeoFenceLocationRows = (previousRows = [], incomingRows = []) => {
  const rowsByUser = new Map();
  [...(Array.isArray(previousRows) ? previousRows : []), ...(Array.isArray(incomingRows) ? incomingRows : [])]
    .forEach((location, index) => {
      const userId = String(location?.user_id || '').trim();
      if (!userId || location?.is_admin_team_marker) return;
      if (!rowsByUser.has(userId)) rowsByUser.set(userId, []);
      rowsByUser.get(userId).push({ location, index });
    });

  return [...rowsByUser.entries()].map(([userId, entries]) => {
    const sorted = [...entries].sort((a, b) => {
      const aTime = geoFenceLocationTimestampMs(a.location);
      const bTime = geoFenceLocationTimestampMs(b.location);
      if (aTime == null && bTime != null) return 1;
      if (aTime != null && bTime == null) return -1;
      if (aTime !== bTime) return Number(bTime || 0) - Number(aTime || 0);
      return b.index - a.index;
    });
    const latest = sorted[0]?.location || {};
    const latestValidCoordinate = sorted.find(({ location }) => isMappableGeoFenceLocation(location))?.location;
    if (!latestValidCoordinate) return { ...latest, user_id: userId };
    return {
      ...latestValidCoordinate,
      ...latest,
      user_id: userId,
      lat: latestValidCoordinate.lat,
      lng: latestValidCoordinate.lng,
    };
  });
};

export const newestGeoFenceLocations = (locationHistory = []) => (
  mergeGeoFenceLocationRows([], locationHistory)
);

const GEO_FENCE_REAL_INCIDENT_STATUSES = new Set([
  'open',
  'acknowledged',
  'in_progress',
  'active',
  'escalated',
]);

export const isGeoFenceOperationalIncident = (
  incident,
  { readOnly = false, scenarioMode = false } = {},
) => {
  if (!incident || readOnly || scenarioMode || incident?.is_active === false) return false;
  if (incident?.is_scenario || incident?.is_planned_event) return false;
  const status = String(incident?.status || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (incident?.is_exercise) return status === 'active';
  return GEO_FENCE_REAL_INCIDENT_STATUSES.has(status);
};

export const createGeoFenceDispatchId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `geo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getOrCreateGeoFenceDispatchId = (registry, actionType) => {
  const key = String(actionType || 'action');
  if (!registry || typeof registry !== 'object') return createGeoFenceDispatchId();
  if (!registry[key]) registry[key] = createGeoFenceDispatchId();
  return registry[key];
};

export const buildGeoFenceTargeting = (userIds = [], fence = {}) => {
  const lat = finiteNumber(fence?.center?.lat);
  const lng = finiteNumber(fence?.center?.lng);
  const radiusKm = finiteNumber(fence?.radiusKm);
  return {
    dispatchId: String(fence?.dispatchId || createGeoFenceDispatchId()),
    userIds: [...new Set((userIds || []).map(String).filter(Boolean))],
    center: lat == null || lng == null ? null : { lat, lng },
    radiusKm: radiusKm == null ? 0 : radiusKm,
  };
};

const responseLayers = (response) => [response, response?.data, response?.data?.data]
  .filter((candidate) => candidate && typeof candidate === 'object');

const valueFromLayers = (layers, keys) => {
  for (const layer of [...layers].reverse()) {
    for (const key of keys) {
      if (layer?.[key] != null) return layer[key];
    }
  }
  return null;
};

const numericField = (layers, keys) => {
  const value = valueFromLayers(layers, keys);
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const manualNotificationOutcome = (response, requestedCount = 0) => {
  const layers = responseLayers(response);
  const failedRecipients = valueFromLayers(layers, ['failed_recipients', 'failedRecipients']);
  const failedCount = numericField(layers, ['failed_count', 'failedCount', 'failure_count', 'failureCount'])
    ?? (Array.isArray(failedRecipients) ? failedRecipients.length : 0);
  const deliveredCount = numericField(layers, [
    'delivered_count',
    'deliveredCount',
    'recipient_count',
    'recipientCount',
    'sent_count',
    'sentCount',
    'notified_count',
    'notifiedCount',
    'success_count',
    'successCount',
  ]);
  const timelineItemId = valueFromLayers(layers, ['timeline_item_id', 'timelineItemId']);
  const transportSuccess = Boolean(response) && response?.status !== false;
  const baseSuccess = transportSuccess
    && layers.every((layer) => layer?.status !== false && layer?.success !== false);
  const partial = transportSuccess && failedCount > 0 && Number(deliveredCount || 0) > 0;
  const ok = baseSuccess && failedCount === 0;
  return {
    ok,
    partial,
    deliveredCount: deliveredCount == null && ok ? Number(requestedCount || 0) : deliveredCount,
    failedCount,
    timelineItemId,
    message: valueFromLayers(layers, ['message', 'detail']) || '',
  };
};

export const timelineWriteSucceeded = (response) => Boolean(response)
  && responseLayers(response).every((layer) => (
    layer?.status !== false && layer?.success !== false
  ));

export const shouldUseGeoFenceRecipientCreate = (editingItem, targeting, selections) => (
  !editingItem
  && Boolean(targeting?.dispatchId)
  && Array.isArray(selections)
  && selections.length > 0
);

export const announceIncidentTimelineChange = (incidentId, changeType = 'geo_fence_action_created') => {
  if (typeof window === 'undefined' || !incidentId) return;
  window.dispatchEvent(new CustomEvent('timelineRefresh', {
    detail: { incident_id: incidentId, change_type: changeType },
  }));
};
